<template>
  <div class="content">
    <div class="md-layout">
      <div class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-100">

        <page-header
          title="Reports"
          subtitle="Wallet and transaction reports you've requested. Each one is generated in
            the background and delivered to your inbox as a secure download link."
        >
          <template #actions>
            <app-button @click="$refs.requestModal.open()">Request report</app-button>
          </template>
        </page-header>

        <div class="report-card">
          <div v-if="loading && !reports.length" class="text-center loading-block">
            <md-progress-spinner
              md-mode="indeterminate"
              :md-diameter="28"
              :md-stroke="3"
            ></md-progress-spinner>
          </div>

          <template v-else>
            <md-table class="report-table">
              <md-table-row>
                <md-table-head>Report</md-table-head>
                <md-table-head>Requested</md-table-head>
                <md-table-head>Status</md-table-head>
                <md-table-head>Rows</md-table-head>
                <md-table-head>Size</md-table-head>
              </md-table-row>

              <md-table-row v-if="!reports.length">
                <md-table-cell colspan="5">
                  <p>No reports requested yet.</p>
                </md-table-cell>
              </md-table-row>

              <md-table-row
                v-for="report in reports"
                :key="report.id"
                class="report-row"
                @click="openReport(report.id)"
              >
                <md-table-cell>
                  <div class="rtype">{{ reportTypeLabel(report) }}</div>
                  <div class="period">{{ periodLabel(report) }}</div>
                </md-table-cell>
                <md-table-cell>
                  {{ format(report.created_at, 'DD/MM/YYYY hh:mm a') }}
                </md-table-cell>
                <md-table-cell>
                  <status-pill
                    :variant="reportStatusVariant(report.status)"
                    :label="reportStatusLabel(report.status)"
                  />
                </md-table-cell>
                <md-table-cell>
                  {{ report.row_count != null ? commaNumber(report.row_count) : '—' }}
                </md-table-cell>
                <md-table-cell>
                  {{ report.file_size != null ? formatBytes(report.file_size) : '—' }}
                </md-table-cell>
              </md-table-row>
            </md-table>

            <div class="report-card__foot" v-if="hasMore">
              <app-button variant="ghost" :disabled="loading" @click="loadMore()">
                {{ loading ? 'Loading…' : 'Load more' }}
              </app-button>
            </div>
          </template>
        </div>

      </div>
    </div>

    <request-modal
      ref="requestModal"
      @created="onCreated"
      @error="onCreateError"
    />

    <md-snackbar
      md-position="center"
      :md-duration="6000"
      :md-active.sync="snackBar.show"
      md-persistent
    >
      <span>{{ snackBar.message }}</span>
    </md-snackbar>
  </div>
</template>

<script>
import { format } from 'date-fns';
import http from '../../utils/Http.util';
import { commaNumber } from '../../utils/Formatter.util';
import { reportStatusLabel, reportStatusVariant } from '../../utils/reportStatus.util';
import PageHeader from '../../components/ui/PageHeader.vue';
import StatusPill from '../../components/ui/StatusPill.vue';
import AppButton from '../../components/ui/AppButton.vue';
import RequestModal from './RequestModal.vue';

const REPORT_CHANNEL = 'apix';

export default {
  name: 'reports-list',
  components: {
    PageHeader,
    StatusPill,
    AppButton,
    RequestModal,
  },
  data() {
    return {
      reports: [],
      loading: true,
      cursor: null,
      hasMore: false,
      snackBar: {
        show: false,
        message: null,
      },
    };
  },
  async mounted() {
    await this.fetchReports();
  },
  methods: {
    format,
    commaNumber,
    reportStatusLabel,
    reportStatusVariant,
    formatBytes(bytes) {
      const kb = bytes / 1024;
      if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
      return `${(kb / 1024).toFixed(1)} MB`;
    },
    reportTypeLabel(report) {
      return report.report_type === 'wallet' ? 'Wallet report' : 'Transaction report';
    },
    periodLabel(report) {
      const { filters } = report;
      if (!filters || !filters.startDate) return '';
      const start = format(filters.startDate, 'D MMM YYYY');
      const end = format(filters.endDate, 'D MMM YYYY');
      return start === end ? start : `${start} – ${end}`;
    },
    openReport(id) {
      this.$router.push(`/reports/${id}`);
    },
    onCreated(report) {
      this.$router.push(`/reports/${report.id}`);
    },
    onCreateError(message) {
      this.snackBar = { show: true, message };
    },
    async fetchReports() {
      this.loading = true;
      try {
        const { data, meta } = await http.authReq('/dashboard/reports', {
          channel: REPORT_CHANNEL,
          ...(this.cursor ? { cursor: this.cursor } : {}),
        });
        this.reports = [...this.reports, ...data];
        this.cursor = meta.nextCursor;
        this.hasMore = meta.hasMore;
      } catch (err) {
        this.snackBar = { show: true, message: err.message || 'We could not load your reports.' };
      } finally {
        this.loading = false;
      }
    },
    loadMore() {
      return this.fetchReports();
    },
  },
};
</script>

<style scoped>
.report-card {
  background: var(--surface);
  border-radius: var(--radius);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
}
.report-card__foot {
  display: flex;
  justify-content: center;
  padding: 14px;
  border-top: 1px solid var(--border);
}
.loading-block { padding: 60px 0; }

.report-row { cursor: pointer; }
.rtype { font-weight: 700; }
.period { color: var(--faint); font-size: 12px; margin-top: 2px; }

/* Brand the table header band the way this template's own
   table-header-color prop would, if vue-material actually implemented it. */
.report-table >>> .md-table-row:first-child {
  background: var(--brand);
}
.report-table >>> .md-table-row:first-child .md-table-head-label {
  color: var(--brand-ink);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 11px;
}
/* The app's own global _tables.scss forces every row's hover background
   to #f5f5f5 with !important — without this, the branded header row
   flashes gray on hover since it isn't actually clickable. */
.report-table >>> .md-table-row:first-child:hover {
  background: var(--brand) !important;
}
</style>
